const CHUNK_RELOAD_FLAG_KEY = "chunk_error_reloaded";

// Vite doesn't expose a typed ChunkLoadError class — dynamic import failures
// surface as plain Errors/TypeErrors with browser-native messages.
// These patterns cover Chrome, Firefox, and Safari across Vite versions.
const VITE_CHUNK_LOAD_ERROR_PATTERNS = [
  "Failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "Loading chunk",
] as const;

/**
 * Returns true if the error is a failed dynamic import / chunk load failure.
 * Covers both Vite ("Failed to fetch dynamically imported module") and
 * webpack ("ChunkLoadError") error shapes.
 */
export function isChunkLoadError(error: unknown): error is Error {
  if (!(error instanceof Error)) return false;
  return VITE_CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => error.message.includes(pattern));
}

/**
 * Attempts a single hard reload to recover from a stale-cache chunk error.
 * A session flag prevents reload loops: returns true when a reload is
 * triggered, false when one has already been attempted this session
 * (meaning the error is persistent and should be shown to the user).
 */
export function handleChunkLoadError(): boolean {
  const hasReloaded = sessionStorage.getItem(CHUNK_RELOAD_FLAG_KEY) === "true";
  if (!hasReloaded) {
    sessionStorage.setItem(CHUNK_RELOAD_FLAG_KEY, "true");
    window.location.reload();
    return true;
  }
  return false;
}

/**
 * Clears the session reload flag so the next manual refresh can trigger
 * the auto-reload guard again if needed.
 */
export function clearChunkReloadFlag(): void {
  sessionStorage.removeItem(CHUNK_RELOAD_FLAG_KEY);
}
